(() => {
  const COMPONENT_NAME = "product-complementary";
  const SECTION_ID = "perfect-hue-collection-page";
  const MAX_PAGES_PER_COLLECTION = 8;

  if (customElements.get(COMPONENT_NAME)) return;

  // Keep in sync with colour-family expansion in snippets/product-matches-colour.liquid
  const COLOUR_GROUP_TAGS = new Set([
    "red",
    "pink",
    "orange",
    "yellow",
    "green",
    "blue",
    "purple",
    "brown",
    "black",
    "grey",
    "gray",
    "white",
  ]);

  const COLOUR_FAMILIES = {
    red: [
      "scarlet",
      "cranberry",
      "poppy",
      "granita",
      "berry",
      "chilli",
      "chili",
      "firecracker",
      "punch",
      "strawberry",
      "cherry",
      "ruby",
      "burgundy",
    ],
    pink: [
      "pink",
      "rose",
      "coral",
      "carnation",
      "lemonade",
      "orchid",
      "blush",
      "magenta",
      "fuchsia",
    ],
    orange: ["orange", "jaffa", "bran", "tangerine", "apricot", "peach"],
    yellow: ["yellow", "corn", "saffron", "lemon", "mustard", "gold", "honey"],
    green: [
      "green",
      "matcha",
      "avocado",
      "lime",
      "teal",
      "artichoke",
      "olive",
      "khaki",
      "military",
      "sage",
      "mint",
      "fern",
    ],
    blue: [
      "blue",
      "navy",
      "airforce",
      "midnight",
      "malibu",
      "petrol",
      "indigo",
      "marine",
      "denim",
      "adriatic",
      "coastal",
      "sky",
      "cobalt",
      "royal",
    ],
    purple: [
      "purple",
      "plum",
      "mulberry",
      "thistle",
      "majestic",
      "violet",
      "lilac",
      "lavender",
      "grape",
    ],
    brown: [
      "brown",
      "taupe",
      "coco",
      "tan",
      "chocolate",
      "rocky",
      "camel",
      "mocha",
      "espresso",
    ],
    black: ["black", "graphite", "charcoal", "onyx", "ink"],
    grey: ["grey", "gray", "smoke", "silver", "slate", "ash"],
    white: ["white", "cream", "ivory", "ecru", "off-white"],
  };

  const COLOUR_MODIFIERS = [
    "tonal",
    "nylon",
    "recycled",
    "canvas",
    "leather",
    "cotton",
    "denim",
    "suede",
    "mesh",
    "ripstop",
  ];

  const normalizeColourNeedle = (value) => {
    let needle = (value || "").trim().toLowerCase();
    if (!needle) return "";

    for (const modifier of COLOUR_MODIFIERS) {
      needle = needle.replace(new RegExp(`\\s+${modifier}\\b`, "g"), "").trim();
    }

    return needle;
  };

  const resolveColourFamilyNeedles = (colourNeedle) => {
    const needle = (colourNeedle || "").trim().toLowerCase();
    if (!needle) return [];

    const lookup = normalizeColourNeedle(needle) || needle;
    const needles = new Set([needle]);

    if (COLOUR_GROUP_TAGS.has(lookup)) {
      needles.add(lookup);
      if (lookup === "grey") needles.add("gray");
      if (lookup === "gray") needles.add("grey");
      return [...needles];
    }

    let familyGroup = "";

    for (const [group, members] of Object.entries(COLOUR_FAMILIES)) {
      for (const member of members) {
        if (lookup === member || lookup.includes(member)) {
          familyGroup = group;
          break;
        }
      }
      if (familyGroup) break;
    }

    if (!familyGroup) {
      if (lookup.includes("red")) familyGroup = "red";
      else if (lookup.includes("blue")) familyGroup = "blue";
      else if (lookup.includes("purple")) familyGroup = "purple";
    }

    if (familyGroup) {
      needles.add(familyGroup);
      if (familyGroup === "grey") needles.add("gray");
    }

    return [...needles];
  };

  const productTitleFromItem = (item) => {
    const fromDataset = (item.dataset.productTitle || "").trim();
    if (fromDataset) return fromDataset;

    return (
      item.querySelector(".product-card-compact__title")?.textContent || ""
    ).trim();
  };

  // Keep in sync with snippets/product-matches-colour.liquid skip rules.
  const isExcludedPerfectHueTitle = (title) => {
    const titleDown = String(title || "")
      .trim()
      .toLowerCase();

    if (!titleDown) return false;
    if (titleDown.includes("bundle")) return true;
    if (titleDown.includes("multipack")) return true;
    if (titleDown.includes("pack") && !titleDown.includes("backpack")) {
      return true;
    }

    return false;
  };

  const matchesSingleColour = (item, needle) => {
    if (!needle) return false;

    const colourName = (item.dataset.colourName || "").trim().toLowerCase();
    const titleColour = (item.dataset.titleColour || "").trim().toLowerCase();
    const tags = (item.dataset.tags || "")
      .split(",")
      .map((tag) => tag.trim().toLowerCase())
      .filter(Boolean);

    if (colourName && colourName === needle) return true;
    if (tags.includes(needle)) return true;
    if (titleColour && titleColour === needle) return true;
    if (titleColour && titleColour.includes(needle)) return true;
    if (titleColour && needle.includes(titleColour)) return true;

    const words = needle
      .replace(/-/g, " ")
      .split(/\s+/)
      .filter((word) => word.length >= 3);

    for (const word of words) {
      if (tags.includes(word)) return true;
      if (titleColour === word || titleColour.includes(word)) return true;
      if (colourName === word || colourName.includes(word)) return true;
    }

    return false;
  };

  const matchesColour = (item, colourNeedle, extraNeedles = []) => {
    if (isExcludedPerfectHueTitle(productTitleFromItem(item))) return false;

    const needles = new Set([
      ...resolveColourFamilyNeedles(colourNeedle),
      ...extraNeedles
        .map((value) =>
          String(value || "")
            .trim()
            .toLowerCase(),
        )
        .filter(Boolean),
    ]);

    for (const needle of needles) {
      if (matchesSingleColour(item, needle)) return true;
    }

    return false;
  };

  const discoverGroupTags = (item) => {
    return (item.dataset.tags || "")
      .split(",")
      .map((tag) => tag.trim().toLowerCase())
      .filter((tag) => COLOUR_GROUP_TAGS.has(tag));
  };

  const parseCollections = (raw) => {
    if (!raw) return [];

    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? parsed.map((handle) => String(handle || "").trim()).filter(Boolean)
        : [];
    } catch {
      return [];
    }
  };

  const extractSectionHtml = (payload) => {
    if (!payload || typeof payload !== "object") return "";
    return payload[SECTION_ID] || Object.values(payload)[0] || "";
  };

  class ProductComplementary extends HTMLElement {
    #abortController = null;
    #fillControllers = new Map();

    connectedCallback() {
      this.#abortController = new AbortController();
      void this.#initialize();
    }

    disconnectedCallback() {
      for (const controller of this.#fillControllers.values()) {
        controller.abort();
      }
      this.#fillControllers.clear();
      this.#abortController?.abort();
      this.#abortController = null;
    }

    async #initialize() {
      this.#relocateSwatches();

      const activeButton = this.#resolveActiveSwatch();
      if (activeButton) {
        this.#setActiveSwatch(activeButton);
        await this.#showPanel(activeButton.dataset.perfectHueSwatch || "");
      }

      const { signal } = this.#abortController || {};
      if (!signal || signal.aborted) return;

      // Reveal header swatches only after relocate + active panel fill.
      this.#revealSwatches();
      this.dataset.perfectHueReady = "true";

      this.#prefetchInactivePanels();

      for (const button of this.querySelectorAll("[data-perfect-hue-swatch]")) {
        button.addEventListener("click", () => this.#onSwatchClick(button), {
          signal,
        });
      }
    }

    #relocateSwatches() {
      const host = this.querySelector("[data-perfect-hue-swatches]");
      if (!host) return;

      const swatches = this.querySelectorAll("[data-perfect-hue-swatch]");
      for (const swatch of swatches) {
        host.appendChild(swatch);
      }
    }

    #revealSwatches() {
      const host = this.querySelector("[data-perfect-hue-swatches]");
      const swatches = host
        ? host.querySelectorAll("[data-perfect-hue-swatch]")
        : this.querySelectorAll("[data-perfect-hue-swatch]");

      for (const swatch of swatches) {
        swatch.hidden = false;
      }
    }

    #resolveActiveSwatch() {
      const matchedSwatch = this.querySelector(
        '[data-perfect-hue-swatch][data-perfect-hue-matched="true"]',
      );
      const firstSwatch = this.querySelector("[data-perfect-hue-swatch]");
      const anyActive = this.querySelector(
        ".product-complementary__colour--active",
      );

      return matchedSwatch || anyActive || firstSwatch;
    }

    #prefetchInactivePanels() {
      for (const panel of this.querySelectorAll("[data-perfect-hue-panel]")) {
        if (!panel.hidden) continue;
        void this.#fillPanelFromCollections(panel, { quiet: true });
      }
    }

    #onSwatchClick(button) {
      const colourId = button.dataset.perfectHueSwatch || "";
      if (!colourId) return;

      this.#setActiveSwatch(button);
      void this.#showPanel(colourId);
    }

    #setActiveSwatch(activeButton) {
      for (const button of this.querySelectorAll("[data-perfect-hue-swatch]")) {
        const isActive = button === activeButton;
        button.classList.toggle(
          "product-complementary__colour--active",
          isActive,
        );
        button.setAttribute("aria-pressed", isActive ? "true" : "false");
      }
    }

    async #showPanel(colourId) {
      const panels = this.querySelectorAll("[data-perfect-hue-panel]");
      if (panels.length === 0) return;

      const fills = [];

      for (const panel of panels) {
        const isActive = panel.dataset.perfectHuePanel === colourId;
        panel.hidden = !isActive;

        if (!isActive) continue;

        const list = panel.querySelector("[data-complementary-list]");
        const alreadyFilled = panel.dataset.perfectHueFilled === "true";

        if (list && alreadyFilled) {
          list.classList.add("product-complementary__list--fade");
          requestAnimationFrame(() => {
            list.classList.remove("product-complementary__list--fade");
          });
        }

        fills.push(this.#fillPanelFromCollections(panel));
      }

      await Promise.all(fills);
    }

    #createSkeletonItem() {
      const item = document.createElement("div");
      item.className =
        "product-complementary__item product-complementary__item--skeleton";
      item.setAttribute("role", "presentation");
      item.setAttribute("aria-hidden", "true");
      item.innerHTML = `
        <div class="product-complementary__skeleton-media"></div>
        <div class="product-complementary__skeleton-body">
          <div class="product-complementary__skeleton-line"></div>
          <div class="product-complementary__skeleton-line product-complementary__skeleton-line--short"></div>
        </div>
      `;
      return item;
    }

    #setLoadingPlaceholders(list, missingCount) {
      this.#clearLoadingPlaceholders(list);

      if (missingCount < 1) return;

      list.classList.add("product-complementary__list--loading");
      list.setAttribute("aria-busy", "true");

      for (let i = 0; i < missingCount; i += 1) {
        list.appendChild(this.#createSkeletonItem());
      }
    }

    #clearLoadingPlaceholders(list) {
      if (!list) return;

      for (const skeleton of list.querySelectorAll(
        ".product-complementary__item--skeleton",
      )) {
        skeleton.remove();
      }

      list.classList.remove("product-complementary__list--loading");
      list.removeAttribute("aria-busy");
    }

    #revealItems(list, items) {
      if (!list || items.length === 0) return;

      const fragment = document.createDocumentFragment();

      for (const item of items) {
        item.classList.add("product-complementary__item--enter");
        fragment.appendChild(item);
      }

      list.appendChild(fragment);

      requestAnimationFrame(() => {
        for (const item of items) {
          item.classList.add("product-complementary__item--entered");
        }
      });
    }

    #seedFillStateFromList(list) {
      const seen = new Set();
      const seenCategories = new Set();
      const discoveredGroups = new Set();

      for (const item of list.querySelectorAll(
        ".product-complementary__item:not(.product-complementary__item--skeleton)",
      )) {
        const link = item.querySelector("a[href*='/products/']");
        const match = link
          ?.getAttribute("href")
          ?.match(/\/products\/([^/?#]+)/);
        const handle = item.dataset.productHandle || match?.[1] || "";

        if (handle) {
          seen.add(handle);
          item.dataset.productHandle = handle;
        }

        const categoryKey = (item.dataset.categoryKey || "")
          .trim()
          .toLowerCase();
        if (categoryKey) seenCategories.add(categoryKey);

        for (const group of discoverGroupTags(item)) {
          discoveredGroups.add(group);
        }
      }

      const count = list.querySelectorAll(
        ".product-complementary__item:not(.product-complementary__item--skeleton)",
      ).length;

      return { seen, seenCategories, discoveredGroups, count };
    }

    #buildCollectionPageUrl(collectionHandle, page) {
      const requestUrl = new URL(
        `/collections/${encodeURIComponent(collectionHandle)}`,
        window.location.origin,
      );
      requestUrl.searchParams.set("page", String(page));
      requestUrl.searchParams.set("sections", SECTION_ID);

      const previewThemeId = new URLSearchParams(window.location.search).get(
        "preview_theme_id",
      );

      if (previewThemeId) {
        requestUrl.searchParams.set("preview_theme_id", previewThemeId);
      }

      return requestUrl;
    }

    async #fetchCollectionPage(collectionHandle, page, signal) {
      const response = await fetch(
        this.#buildCollectionPageUrl(collectionHandle, page).toString(),
        { signal },
      );

      if (!response.ok) return null;

      const payload = await response.json();
      const html = extractSectionHtml(payload);
      if (!html) return null;

      const doc = new DOMParser().parseFromString(html, "text/html");
      const pageRoot = doc.querySelector("[data-perfect-hue-page]");
      const items = [
        ...(pageRoot?.querySelectorAll(".product-complementary__item") || []),
      ];

      return {
        items,
        totalPages: Number(pageRoot?.dataset.pages || "1") || 1,
      };
    }

    #collectMatchingPageItems(pageItems, state, options) {
      const { colourKey, excludeHandle, productsToShow } = options;
      const matched = [];

      for (const item of pageItems) {
        if (state.count >= productsToShow) break;

        const handle = item.dataset.productHandle || "";
        if (!handle || state.seen.has(handle)) continue;
        if (excludeHandle && handle === excludeHandle) continue;
        if (!matchesColour(item, colourKey, state.discoveredGroups)) continue;

        const categoryKey = (item.dataset.categoryKey || "")
          .trim()
          .toLowerCase();
        if (categoryKey && state.seenCategories.has(categoryKey)) continue;

        state.seen.add(handle);
        if (categoryKey) state.seenCategories.add(categoryKey);
        for (const group of discoverGroupTags(item)) {
          state.discoveredGroups.add(group);
        }

        matched.push(item);
        state.count += 1;
      }

      return matched;
    }

    #commitFilledItems(list, pending, { quiet }) {
      if (!quiet) {
        this.#clearLoadingPlaceholders(list);
        this.#revealItems(list, pending);
        return;
      }

      for (const item of pending) {
        list.appendChild(item);
      }
    }

    async #fillPanelFromCollections(panel, { quiet = false } = {}) {
      if (panel.dataset.perfectHuePaginate !== "true") return;
      if (panel.dataset.perfectHueFilled === "true") return;

      const list = panel.querySelector("[data-complementary-list]");
      if (!list) return;

      const productsToShow = Number(panel.dataset.productsToShow || "3") || 3;
      const colourKey = panel.dataset.colourKey || "";
      const excludeHandle = panel.dataset.excludeHandle || "";
      const collections = parseCollections(panel.dataset.collections || "[]");

      if (!colourKey || collections.length === 0) {
        panel.dataset.perfectHueFilled = "true";
        return;
      }

      const state = this.#seedFillStateFromList(list);
      if (state.count >= productsToShow) {
        panel.dataset.perfectHueFilled = "true";
        return;
      }

      const panelId = panel.dataset.perfectHuePanel || "";
      this.#fillControllers.get(panelId)?.abort();
      const controller = new AbortController();
      this.#fillControllers.set(panelId, controller);

      const pending = [];
      const needsMore = productsToShow - state.count;
      let aborted = false;
      let sawPageHtml = false;

      if (!quiet) {
        this.#setLoadingPlaceholders(list, needsMore);
      }

      try {
        for (const collectionHandle of collections) {
          if (state.count >= productsToShow) break;

          for (let page = 1; page <= MAX_PAGES_PER_COLLECTION; page += 1) {
            if (state.count >= productsToShow) break;

            const pageResult = await this.#fetchCollectionPage(
              collectionHandle,
              page,
              controller.signal,
            );

            if (!pageResult) break;

            sawPageHtml = true;

            if (pageResult.items.length === 0) break;

            pending.push(
              ...this.#collectMatchingPageItems(pageResult.items, state, {
                colourKey,
                excludeHandle,
                productsToShow,
              }),
            );

            if (page >= pageResult.totalPages) break;
          }
        }

        this.#commitFilledItems(list, pending, { quiet });

        // Mark complete only after at least one page of section HTML was
        // parsed (or the panel was already full). Failed/null section
        // responses must stay retryable on the next swatch activation.
        if (sawPageHtml || state.count >= productsToShow) {
          panel.dataset.perfectHueFilled = "true";
        }
      } catch (error) {
        if (error?.name === "AbortError") {
          aborted = true;
        } else if (!quiet) {
          this.#clearLoadingPlaceholders(list);
        }
      } finally {
        const stillOwner = this.#fillControllers.get(panelId) === controller;

        if (stillOwner) {
          this.#fillControllers.delete(panelId);
        }

        // Aborted fills must not mark the panel complete — a replacement
        // request (e.g. visible fill after quiet prefetch) still needs to run.
        // Only clear skeletons when we still own the panel; clearing after a
        // superseding fill would wipe that request's loading placeholders.
        if (aborted && !quiet && stillOwner) {
          this.#clearLoadingPlaceholders(list);
        }
      }
    }
  }

  customElements.define(COMPONENT_NAME, ProductComplementary);
})();
